import { useEffect, useRef, useState } from "react";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Sketch from "react-p5";

import { sketch } from "controllers/sketch";

import styles from "./Board.module.css";

const INITIAL_LAYOUT = {
  gridSize: 28,
  obstacleDensity: 0.24,
};

const INITIAL_RUNTIME = {
  paused: false,
  stepsPerFrame: 2,
};

const INITIAL_TELEMETRY = {
  status: "idle",
  stepCount: 0,
  exploredCount: 0,
  frontierCount: 0,
  pathLength: 0,
  startLabel: "--",
  endLabel: "--",
};

const STATUS_LABELS = {
  idle: "Booting",
  searching: "Scanning",
  found: "Route Locked",
  blocked: "No Route",
};

const legendItems = [
  ["Start", "legendStart"],
  ["Goal", "legendEnd"],
  ["Frontier", "legendOpen"],
  ["Scanned", "legendClosed"],
  ["Current Route", "legendPath"],
  ["Obstacle", "legendWall"],
];

function layoutsMatch(firstLayout, secondLayout) {
  return (
    firstLayout.gridSize === secondLayout.gridSize &&
    firstLayout.obstacleDensity === secondLayout.obstacleDensity
  );
}

function Board() {
  const [draftLayout, setDraftLayout] = useState(INITIAL_LAYOUT);
  const [appliedLayout, setAppliedLayout] = useState(INITIAL_LAYOUT);
  const [runtime, setRuntime] = useState(INITIAL_RUNTIME);
  const [telemetry, setTelemetry] = useState(INITIAL_TELEMETRY);
  const appliedLayoutRef = useRef(INITIAL_LAYOUT);
  const runtimeRef = useRef(INITIAL_RUNTIME);
  const controllerRef = useRef(null);

  if (!controllerRef.current) {
    controllerRef.current = sketch({
      getLayoutOptions: () => ({
        columns: appliedLayoutRef.current.gridSize,
        rows: appliedLayoutRef.current.gridSize,
        wallProbability: appliedLayoutRef.current.obstacleDensity,
      }),
      getRuntimeOptions: () => runtimeRef.current,
      onStatsChange: setTelemetry,
    });
  }

  useEffect(() => {
    appliedLayoutRef.current = appliedLayout;
  }, [appliedLayout]);

  useEffect(() => {
    runtimeRef.current = runtime;
  }, [runtime]);

  const { draw, setup, windowResized, dispatch } = controllerRef.current;
  const isLayoutDirty = !layoutsMatch(draftLayout, appliedLayout);
  const statusLabel = STATUS_LABELS[telemetry.status] ?? STATUS_LABELS.idle;

  function updateRuntime(nextRuntime) {
    runtimeRef.current = nextRuntime;
    setRuntime(nextRuntime);
  }

  function applyLayout() {
    const nextLayout = { ...draftLayout };

    appliedLayoutRef.current = nextLayout;
    setAppliedLayout(nextLayout);
    dispatch({ type: "regenerate" });
  }

  function togglePaused() {
    updateRuntime({
      ...runtimeRef.current,
      paused: !runtimeRef.current.paused,
    });
  }

  function changeSpeed(nextSpeed) {
    updateRuntime({
      ...runtimeRef.current,
      stepsPerFrame: nextSpeed,
    });
  }

  return (
    <main className={styles.container}>
      <div className={styles.shell}>
        <section className={styles.stagePanel}>
          <div className={styles.stageHeader}>
            <div>
              <p className={styles.eyebrow}>Algorithm Map Room</p>
              <h1 className={styles.title}>Path Finder</h1>
              <p className={styles.intro}>
                Tune the search space, watch A* advance across the grid, then
                replay the same obstacle field or roll a new one.
              </p>
            </div>
            <div className={styles.links}>
              <a
                target="_blank"
                rel="noopener noreferrer"
                href="https://www.linkedin.com/in/luca-leiva-gonzalez/"
              >
                <FontAwesomeIcon
                  className={styles.icon}
                  icon={["fab", "linkedin"]}
                  aria-hidden="true"
                />
                <span className={styles.hidden}>LinkedIn Profile</span>
              </a>
              <a
                target="_blank"
                rel="noopener noreferrer"
                href="https://github.com/eleleiva"
              >
                <FontAwesomeIcon
                  className={styles.icon}
                  icon={["fab", "github"]}
                  aria-hidden="true"
                />
                <span className={styles.hidden}>GitHub Profile</span>
              </a>
              <a
                target="_blank"
                rel="noopener noreferrer"
                href="https://github.com/eleleiva/path-finder"
              >
                <FontAwesomeIcon
                  className={styles.icon}
                  icon="code"
                  aria-hidden="true"
                />
                <span className={styles.hidden}>GitHub Repository</span>
              </a>
            </div>
          </div>

          <div className={styles.canvasFrame}>
            <div className={styles.canvasContainer}>
              <Sketch draw={draw} setup={setup} windowResized={windowResized} />
            </div>
          </div>

          <div className={styles.legend}>
            {legendItems.map(([label, swatchClass]) => (
              <div key={label} className={styles.legendItem}>
                <span
                  className={`${styles.legendSwatch} ${styles[swatchClass]}`}
                />
                <span>{label}</span>
              </div>
            ))}
          </div>
        </section>

        <aside className={styles.controlPanel}>
          <div className={styles.panelSection}>
            <div className={styles.statusRow}>
              <p className={styles.sectionLabel}>Search Status</p>
              <span
                className={`${styles.statusBadge} ${styles[telemetry.status] || styles.idle}`}
              >
                {statusLabel}
              </span>
            </div>
            <div className={styles.readouts}>
              <div className={styles.readout}>
                <span className={styles.readoutLabel}>Route</span>
                <strong>{telemetry.pathLength}</strong>
              </div>
              <div className={styles.readout}>
                <span className={styles.readoutLabel}>Steps</span>
                <strong>{telemetry.stepCount}</strong>
              </div>
              <div className={styles.readout}>
                <span className={styles.readoutLabel}>Frontier</span>
                <strong>{telemetry.frontierCount}</strong>
              </div>
              <div className={styles.readout}>
                <span className={styles.readoutLabel}>Scanned</span>
                <strong>{telemetry.exploredCount}</strong>
              </div>
            </div>
            <dl className={styles.coordinateList}>
              <div>
                <dt>Start</dt>
                <dd>{telemetry.startLabel}</dd>
              </div>
              <div>
                <dt>Goal</dt>
                <dd>{telemetry.endLabel}</dd>
              </div>
            </dl>
          </div>

          <div className={styles.panelSection}>
            <p className={styles.sectionLabel}>Controls</p>
            <div className={styles.buttonRow}>
              <button
                type="button"
                className={styles.primaryButton}
                onClick={togglePaused}
              >
                {runtime.paused ? "Resume Scan" : "Pause Scan"}
              </button>
              <button
                type="button"
                className={styles.secondaryButton}
                onClick={() => dispatch({ type: "reset" })}
              >
                Reset Route
              </button>
            </div>
            <div className={styles.buttonRow}>
              <button
                type="button"
                className={styles.secondaryButton}
                onClick={() => dispatch({ type: "regenerate" })}
              >
                Regenerate Map
              </button>
              <button
                type="button"
                className={styles.secondaryButton}
                onClick={applyLayout}
                disabled={!isLayoutDirty}
              >
                Apply Layout
              </button>
            </div>

            <label className={styles.controlGroup} htmlFor="grid-size">
              <span>Grid Size</span>
              <span>
                {draftLayout.gridSize} x {draftLayout.gridSize}
              </span>
              <input
                id="grid-size"
                type="range"
                min="14"
                max="48"
                value={draftLayout.gridSize}
                onChange={(event) => {
                  const nextGridSize = Number(event.target.value);

                  setDraftLayout({
                    ...draftLayout,
                    gridSize: nextGridSize,
                  });
                }}
              />
            </label>

            <label className={styles.controlGroup} htmlFor="obstacle-density">
              <span>Obstacle Density</span>
              <span>{Math.round(draftLayout.obstacleDensity * 100)}%</span>
              <input
                id="obstacle-density"
                type="range"
                min="10"
                max="40"
                value={Math.round(draftLayout.obstacleDensity * 100)}
                onChange={(event) => {
                  const nextDensity = Number(event.target.value) / 100;

                  setDraftLayout({
                    ...draftLayout,
                    obstacleDensity: nextDensity,
                  });
                }}
              />
            </label>

            <label className={styles.controlGroup} htmlFor="simulation-speed">
              <span>Scan Speed</span>
              <span>{runtime.stepsPerFrame} steps / frame</span>
              <input
                id="simulation-speed"
                type="range"
                min="1"
                max="12"
                value={runtime.stepsPerFrame}
                onChange={(event) => changeSpeed(Number(event.target.value))}
              />
            </label>
          </div>

          <div className={styles.panelSection}>
            <p className={styles.sectionLabel}>How It Works</p>
            <p className={styles.copy}>
              The renderer only paints the current search snapshot. A separate
              A* engine tracks frontier nodes, scanned cells, and the latest
              candidate route so the logic can be tested without the canvas.
            </p>
            <p className={styles.copy}>
              Movement is 8-directional with diagonal corner clipping blocked,
              using an octile-distance heuristic to keep the scan sharp on dense
              maps.
            </p>
          </div>
        </aside>
      </div>
    </main>
  );
}

export { Board };
